import React from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BuildIcon from '@mui/icons-material/Build';
import PaymentsIcon from '@mui/icons-material/Payments';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import EventIcon from '@mui/icons-material/Event';

// id 별 아이콘. 백엔드 StatusCntDto.id 와 1:1로 맞춘다 - id 가 늘면 여기만 추가하면 된다.
// (KSM ProjectCount 를 bito 의 5개 카운트에 맞게 포팅)
const ICON_MAP = {
  project: AssignmentIcon,
  maintance: BuildIcon,
  collect: PaymentsIcon,
  orderReq: SupportAgentIcon,
  orderPlan: EventIcon,
};

/**
 * 헤더의 "할 일" 뱃지 하나.
 *
 * - fullWidth=false(기본): 데스크톱 헤더용 28px 높이 알약(pill)
 * - fullWidth=true: 모바일 바텀 드로어용 한 줄(아이콘+제목+숫자, 설명은 보조 줄)
 *
 * 링크/설명 문구는 백엔드가 내려주는 project.link / project.tooltip 을 그대로 쓴다.
 */
const ProjectCount = ({ project, fullWidth = false, onSelect }) => {
  const navigate = useNavigate();
  const link = project?.link;
  const count = Number(project?.count || 0);
  const overdue = Number(project?.overdue || 0);
  const hasWork = count > 0 || overdue > 0;
  const Icon = ICON_MAP[project?.id] || AssignmentIcon;

  const handleClick = () => {
    if (!link) return;
    navigate(link);
    if (onSelect) {
      onSelect();
    }
  };

  if (fullWidth) {
    return (
      <Box
        onClick={link ? handleClick : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1,
          borderRadius: 1,
          cursor: link ? 'pointer' : 'default',
          '&:hover': link ? { bgcolor: 'action.hover' } : undefined,
        }}
      >
        <Icon color={hasWork ? 'primary' : 'disabled'} fontSize="small" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600}>
            {project?.title}
          </Typography>
          {project?.tooltip && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block' }}
            >
              {project.tooltip}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color={count > 0 ? 'primary.main' : 'text.disabled'}
          >
            {count}
          </Typography>
          {overdue > 0 && (
            <Typography variant="caption" fontWeight={700} color="error.main">
              지연 {overdue}
            </Typography>
          )}
        </Stack>
      </Box>
    );
  }

  return (
    <Tooltip title={project?.tooltip || project?.title || ''} arrow>
      <Box
        onClick={link ? handleClick : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          height: 28,
          px: 1.25,
          mx: 0.5,
          borderRadius: 14,
          bgcolor: (theme) =>
            hasWork ? alpha(theme.palette.primary.main, 0.08) : 'action.hover',
          cursor: link ? 'pointer' : 'default',
          '&:hover': link ? { filter: 'brightness(0.96)' } : undefined,
        }}
      >
        <Icon fontSize="small" color={hasWork ? 'primary' : 'disabled'} />
        <Typography
          variant="caption"
          color={hasWork ? 'text.primary' : 'text.disabled'}
          noWrap
        >
          {project?.title}
        </Typography>
        <Typography
          variant="caption"
          fontWeight={700}
          color={count > 0 ? 'primary.main' : 'text.disabled'}
        >
          {count}
        </Typography>
        {overdue > 0 && (
          <Typography variant="caption" fontWeight={700} color="error.main">
            · 지연 {overdue}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

export default ProjectCount;
